export type Units = 'metric' | 'imperial';

export const METRES_PER_FOOT = 0.3048;
export const METRES_PER_INCH = 0.0254;

/** Rounds to `digits` decimals and drops trailing zeros. */
function trimNumber(n: number, digits: number): string {
  return n.toFixed(digits).replace(/\.?0+$/, '');
}

/**
 * Formats a length stored in metres: metric `1.25 m` (max. 2 decimals), imperial feet and whole
 * inches `3'6"` (under a foot only inches, `8"`).
 */
export function formatLength(metres: number, units: Units): string {
  if (units === 'metric') return `${trimNumber(metres, 2)} m`;
  const sign = metres < 0 ? '-' : '';
  const totalInches = Math.round(Math.abs(metres) / METRES_PER_INCH);
  const feet = Math.floor(totalInches / 12);
  const inches = totalInches % 12;
  return feet === 0 ? `${sign}${inches}"` : `${sign}${feet}'${inches}"`;
}

const UNIT_FACTORS: Readonly<Record<string, number>> = {
  m: 1,
  cm: 0.01,
  mm: 0.001,
  ft: METRES_PER_FOOT,
  "'": METRES_PER_FOOT,
  in: METRES_PER_INCH,
  '"': METRES_PER_INCH,
};

const NUMBER = String.raw`(-?\d+(?:[.,]\d+)?)`;
const SINGLE = new RegExp(String.raw`^${NUMBER}\s*(m|cm|mm|ft|'|in|")?$`, 'i');
const UNSIGNED = String.raw`(\d+(?:[.,]\d+)?)`;
const FEET_INCHES = new RegExp(
  String.raw`^(-?)${UNSIGNED}\s*(?:ft|')\s*${UNSIGNED}\s*(?:in|")?$`,
  'i',
);

function num(text: string): number {
  return Number(text.replace(',', '.'));
}

/**
 * Parses a length to metres: `1.2 m`, `1,2m`, `120cm`, `85 mm`, `3'6"`, `3 ft 6 in`, `42"`, `4ft`.
 * A bare number uses `defaultUnits` (metres, or feet for imperial). Returns undefined if invalid.
 */
export function parseLength(text: string, defaultUnits: Units = 'metric'): number | undefined {
  const t = text.trim();
  const fi = FEET_INCHES.exec(t);
  if (fi !== null) {
    const metres = num(fi[2]!) * METRES_PER_FOOT + num(fi[3]!) * METRES_PER_INCH;
    return fi[1] === '-' ? -metres : metres;
  }
  const m = SINGLE.exec(t);
  if (m === null) return undefined;
  const unit = m[2]?.toLowerCase() ?? (defaultUnits === 'metric' ? 'm' : 'ft');
  return num(m[1]!) * UNIT_FACTORS[unit]!;
}

/** Normalises an angle in degrees to [0, 360). */
export function normalizeAngle(deg: number): number {
  const r = deg % 360;
  const n = r < 0 ? r + 360 : r;
  // Tiny negative angles round up to exactly 360; `+ 0` turns -0 into 0.
  return n === 360 ? 0 : n + 0;
}

/** Rounds an angle to the nearest multiple of `step` degrees (e.g. 15° rotation steps), normalised. */
export function snapAngle(deg: number, step: number): number {
  return normalizeAngle(Math.round(deg / step) * step);
}
